import PrimaryLoadingButton from "@components/PrimaryLoadingButton";
import { useAuthentication } from "@providers/AuthenticationProvider";
import { useProductDetailContext } from "@providers/ProductDetailProvider/ProductDetailProvider";
import { Body, BodySize, Title, TitleSize } from "@sourceful/design-system";
import {
  ButtonGroup,
  Heading,
  PrimaryButton,
  Link as SourcefulLink,
  Text,
  styled,
} from "@sourceful/shared-components";
import { ProductMain, ProductType } from "@sourceful/shared-types";
import { formatTotalPrice } from "@sourceful/shared-utils/engine-utils";
import toRem from "@styles/postprocess/conversion/to-rem";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { apiConfig } from "../../../../apiConfig";
import { routeToLeadGenUrl } from "../helpers";
import ProductDescription from "./ProductDescription";
import ProductDetailsQuantityAndPrice from "./ProductDetailsQuantityAndPrice";
import ProductTags from "./ProductTags";
import GoogleShopStructuredMarkup from "./GoogleShopStructuredMarkup";

export const FastQuoteWrapper = styled("div", {
  paddingBottom: toRem(15),
});

const ProductDetailsWrapper = styled("div", {});

const RfqFromPrice = styled("div", {
  marginBlock: "$spacing-fluid-block-m",
});

interface ProductDetailsProps {
  product: ProductMain;
  displayConfigureButton: boolean;
}

const ProductDetails = ({ product, displayConfigureButton }: ProductDetailsProps) => {
  const {
    selectedPreset,
    engineMetadata,
    updatePresetQuantity,
    updatePresetSize,
    presetConfigurationEnabled,
    onRequestQuoteClicked,
  } = useProductDetailContext();

  const { isLoading: authLoading } = useAuthentication();

  const isRFQ = product.type === ProductType.RFQ;

  let rfqFromPriceInPence = 0;

  if (isRFQ && product.pricePerUnit) {
    rfqFromPriceInPence = product.pricePerUnit;
  }

  const router = useRouter();

  const triggerRef = React.createRef<HTMLButtonElement>();

  const isDefaultCtaLoading = authLoading || displayConfigureButton === false;

  const renderPrimaryCTA = () => {
    if (isRFQ) {
      return (
        <PrimaryButton
          compact={true}
          borderRadius={"square"}
          data-cy="start-quote-button"
          as="a"
          href={routeToLeadGenUrl(router, product.slug)}
        >
          Request quote
        </PrimaryButton>
      );
    }

    return (
      <PrimaryLoadingButton
        data-cy="request-quote-button"
        isLoading={displayConfigureButton === false || isDefaultCtaLoading}
        disabled={displayConfigureButton === false}
        ref={triggerRef}
        handleClick={() => {
          onRequestQuoteClicked({ preset: selectedPreset });
        }}
        defaultLabel="Request quote"
        loadingMessage={isDefaultCtaLoading ? "Loading product" : "Loading"} //
      />
    );
  };

  return (
    <ProductDetailsWrapper>
      <div data-preset-id={selectedPreset?.id}>
        <Heading level={1} fontSize={3}>
          {product.name}
        </Heading>
        <Text level={3} css={{ color: "#005ae1" }}>
          {product.moq ? <>Minimum order quantity: {product.moq}</> : product.fromPrice}
        </Text>
      </div>
      <ProductDescription description={product?.description} />
      <ProductTags tags={product?.tags ?? []} />

      {presetConfigurationEnabled && engineMetadata && selectedPreset && (
        <ProductDetailsQuantityAndPrice
          selectedPreset={selectedPreset}
          engineMetadata={engineMetadata}
          product={product}
          updatePresetQuantity={updatePresetQuantity}
          updatePresetSize={updatePresetSize}
          selectSizeEnabled={product.type !== ProductType.RFQ}
        />
      )}

      {isRFQ && rfqFromPriceInPence !== 0 && (
        <>
          <RfqFromPrice>
            <Title size={TitleSize.Medium}>
              From {formatTotalPrice(rfqFromPriceInPence, "GBP")}{" "}
              <Body as="span" size={BodySize.Medium}>
                / unit
              </Body>
            </Title>
          </RfqFromPrice>
        </>
      )}

      <GoogleShopStructuredMarkup
        selectedPreset={selectedPreset}
        product={product}
        rfqFromPriceInPence={rfqFromPriceInPence}
        engineMetadata={engineMetadata}
      />

      <ButtonGroup
        appearance="singleColumn"
        css={{ marginTop: toRem(30), marginBottom: toRem(40), gridGap: toRem(20) }}
      >
        {renderPrimaryCTA()}
      </ButtonGroup>
      <Text level={3}>
        <b>Have questions? </b>{" "}
        <Link passHref href={`${apiConfig.gtmSite}/contact`} legacyBehavior>
          <SourcefulLink>Contact us</SourcefulLink>
        </Link>
      </Text>
    </ProductDetailsWrapper>
  );
};

export default ProductDetails;
